import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { usePermission } from '@/features/permissions/usePermission'
import { asCard } from '@/features/permissions/ability'
import type { Card } from '@/types/entities'
import { MatrixPicker } from './MatrixPicker'
import { useSetMatrix } from './MatrixChip'
import { quadrantOf } from './quadrants'

const SKIPPED = 'tapwork.matrix.skipped'
const readSkipped = (): Set<string> => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SKIPPED) ?? '[]') as string[])
  } catch {
    return new Set()
  }
}
const writeSkipped = (ids: Set<string>) => {
  try {
    sessionStorage.setItem(SKIPPED, JSON.stringify([...ids]))
  } catch {
    /* private mode: it will simply ask again */
  }
}

function AskDialog({ card, onDone }: { card: Card; onDone: () => void }) {
  const set = useSetMatrix(card)
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDone() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Is this urgent? Is it important?</DialogTitle>
          <DialogDescription>“{card.title}” has no answer yet. One click is enough, and it helps the team decide what to do first.</DialogDescription>
        </DialogHeader>
        <MatrixPicker value={null} disabled={set.isPending} onChange={(next) => { if (next) set.mutate(next, { onSettled: onDone }) }} />
        <DialogFooter>
          <Button variant="ghost" onClick={onDone}>Skip for now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Asks about an unanswered item at the moment somebody acts on it (starts work, moves it), then carries on with
 * what they were doing whatever they answer. Asked at most once per item per browser session, only of people
 * who may edit the item, and never twice for one that is already answered.
 */
export function useAskMatrix(): { ask: (card: Card, proceed: () => void) => void; dialog: ReactNode } {
  const { can } = usePermission()
  const [pending, setPending] = useState<Card | null>(null)
  const proceedRef = useRef<() => void>(() => {})

  const ask = useCallback(
    (card: Card, proceed: () => void) => {
      const skipped = readSkipped()
      if (quadrantOf(card.customAttrs) || skipped.has(card.id) || !can('update', asCard(card))) {
        proceed()
        return
      }
      proceedRef.current = proceed
      setPending(card)
    },
    [can],
  )

  const done = () => {
    if (pending) writeSkipped(new Set([...readSkipped(), pending.id]))
    setPending(null)
    proceedRef.current()
  }
  return { ask, dialog: pending ? <AskDialog card={pending} onDone={done} /> : null }
}
